import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Portal
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Launch as LaunchIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Link as LinkIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AccountBox as AccountBoxIcon
} from '@mui/icons-material';
import { useDrag, useDrop } from 'react-dnd';

import { Tile } from '../../types';
import { useDashboard } from '../../hooks/useDashboard';
import { useDragDrop } from '../../contexts/DragDropContext';
import IconDisplay from '../IconDisplay/IconDisplay';
import { useTheme } from '@mui/material/styles';

interface TileCardProps {
  tile: Tile;
  editMode: boolean;
  index: number;
  groupId: string;
}

const TileCard: React.FC<TileCardProps> = ({
  tile,
  editMode,
  index,
  groupId
}) => {
  const { updateTile, deleteTile, setSelectedItem, config } = useDashboard();
  const dragDropContext = useDragDrop();
  const { setDraggedItem, setDragType, handleTileDrop, isDragging } = dragDropContext || {};
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showUrlTooltip, setShowUrlTooltip] = useState(false);
  const theme = useTheme();
  
  // Get theme colors
  const themeColors = config?.theme?.customColors || {} as NonNullable<import('../../types').DashboardTheme['customColors']>;

  // Drag functionality
  const [{ isDragSource }, drag, dragPreview] = useDrag({
    type: 'TILE',
    item: () => {
      const dragItem = {
        id: tile.id,
        type: 'tile' as const,
        sourceGroupId: groupId,
        data: tile
      };
      if (setDraggedItem) setDraggedItem(dragItem);
      if (setDragType) setDragType('tile');
      return dragItem;
    },
    end: () => {
      if (setDraggedItem) setDraggedItem(null);
      if (setDragType) setDragType(null);
    },
    collect: (monitor) => ({
      isDragSource: monitor.isDragging(),
    }),
    canDrag: editMode, // Only allow dragging in edit mode
  });

  // Drop functionality (for reordering within the same group)
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'TILE',
    drop: (draggedItem: any) => {
      if (draggedItem.id !== tile.id && handleTileDrop) {
        handleTileDrop(draggedItem.id, groupId, index);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    canDrop: () => editMode, // Only allow dropping in edit mode
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTileClick = () => {
    if (!editMode) {
      window.open(tile.url, tile.openInNewTab ? '_blank' : '_self');
    }
  };

  const handleEdit = () => {
    setSelectedItem({ type: 'tile', id: tile.id });
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete the "${tile.title}" tile?`)) {
      await deleteTile(tile.id);
    }
    handleMenuClose();
  };

  const handleToggleVisibility = async () => {
    await updateTile(tile.id, { isVisible: !tile.isVisible });
    handleMenuClose();
  };

  const handleOpenInNewTab = () => {
    window.open(tile.url, '_blank');
    handleMenuClose();
  };

  const handleOpenInSameTab = () => {
    window.open(tile.url, '_self');
    handleMenuClose();
  };



  // Extract domain from URL for display
  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  return (
    <Card
      ref={(node) => {
        dragPreview(drop(node));
      }}
      onClick={handleTileClick}
      onMouseEnter={() => !editMode && setShowUrlTooltip(true)}
      onMouseLeave={() => setShowUrlTooltip(false)}
      elevation={0}
      sx={{
        height: config?.layout?.itemHeight ? `${config.layout.itemHeight}px` : '120px',
        cursor: editMode ? 'default' : 'pointer',
        border: '1px solid',
        borderColor: editMode ? `${tile.color}40` : 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: themeColors.tileBackground || 'background.paper',
        transform: isDragSource ? 'rotate(2deg) scale(0.97)' : 'none',
        opacity: isDragSource ? 0.8 : (tile.isVisible ? 1 : 0.6),
        zIndex: isDragSource ? 1000 : 'auto',
        '&:hover': {
          transform: editMode ? 'none' : (isDragSource ? 'rotate(2deg) scale(0.97)' : 'translateY(-2px)'),
          boxShadow: editMode 
            ? `0 4px 20px ${tile.color}20` 
            : '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: `${tile.color}60`,
        },
        // Drop feedback
        ...(isOver && canDrop && {
          borderColor: 'primary.main',
          borderWidth: 2,
          backgroundColor: 'primary.50',
          boxShadow: '0 0 20px rgba(25, 118, 210, 0.3)',
        }),
        // Global drag feedback
        ...(isDragging && !isDragSource && {
          borderStyle: 'dashed',
          borderColor: 'primary.light',
          backgroundColor: 'action.hover',
        }),
      }}
    >
      <CardContent sx={{ pb: 2, pt: 2, px: 2 }}>
        {/* Tile Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          {editMode && (
            <Box
              ref={drag}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                p: 0.5,
                borderRadius: 1,
                color: 'text.disabled',
                cursor: 'grab',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: themeColors.editButton || '#3f3f3e',
                  backgroundColor: 'rgba(63, 63, 62, 0.1)',
                },
                '&:active': {
                  cursor: 'grabbing',
                  backgroundColor: 'action.selected',
                },
              }}
            >
              <DragIcon sx={{ fontSize: 16 }} />
            </Box>
          )}
          
          {/* Tile Icon */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              // Only apply background and color for material icons, not for service favicons
              background: tile.iconType === 'service' ? 'transparent' : (themeColors.tileIconBackground || `${tile.color}15`),
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: tile.iconType === 'service' ? 'inherit' : (themeColors.tileIconColor || tile.color),
              flexShrink: 0,
            }}
          >
            <IconDisplay
              icon={tile.icon}
              iconType={tile.iconType}
              iconUrl={tile.iconUrl}
              url={tile.url}
              title={tile.title}
              size={tile.iconType === 'service' ? 32 : 20}
              fallbackIcon="AccountBox"
            />
          </Box>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              component="h4"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: themeColors.tileTitle || 'text.primary',
                fontSize: '1.1rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
                letterSpacing: '-0.01em'
              }}
            >
              {tile.title}
            </Typography>
            
            {tile.description && (
              <Typography 
                variant="body2" 
                sx={{
                  mb: 0.5,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.3,
                  fontSize: '0.8rem',
                  color: themeColors.tileDescription || 'text.secondary',
                }}
              >
                {tile.description}
              </Typography>
            )}
          </Box>

          {editMode && (
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              aria-label="tile options"
              sx={{ 
                p: 1,
                borderRadius: 1.5,
                transition: 'all 0.2s ease',
                color: themeColors.editButton || '#3f3f3e',
                '&:hover': {
                  backgroundColor: 'rgba(63, 63, 62, 0.1)',
                  color: themeColors.editButton || '#3f3f3e',
                }
              }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

      </CardContent>


      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Tile</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleToggleVisibility}>
          <ListItemIcon>
            {tile.isVisible ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {tile.isVisible ? 'Hide Tile' : 'Show Tile'}
          </ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleOpenInNewTab}>
          <ListItemIcon>
            <OpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in New Tab</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleOpenInSameTab}>
          <ListItemIcon>
            <LaunchIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in Same Tab</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Tile</ListItemText>
        </MenuItem>
      </Menu>

      {/* Custom URL Tooltip */}
      {showUrlTooltip && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 16,
              left: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.87)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: 1,
              fontSize: '0.875rem',
              zIndex: 9999,
              maxWidth: '400px',
              wordBreak: 'break-all',
              fontFamily: 'monospace'
            }}
          >
            {tile.url}
          </Box>
        </Portal>
      )}
    </Card>
  );
};

export default TileCard;